import { useEffect, useRef, useState } from 'react'
import { updateMyProfile, changeMyPassword } from '../api'
import { useTabs, useDataVersion, useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useEditLock } from '../layout/useEditLock'
import PasswordStrengthMeter, { isPasswordAcceptable, MIN_PASSWORD_LENGTH } from '../components/PasswordStrengthMeter'
import PasswordInput from '../components/PasswordInput'
import { checkBeforeSave } from '../forms/checkForm'
import { fill, PAGES_PROFILE_TEXTS as T } from '../../shared/texts'

const MIN_PW = MIN_PASSWORD_LENGTH

// Self-service account page (opened from the topbar "Profil" menu). Editing the
// own account via the user management is blocked on purpose (PUT self → 403), so
// name + password changes live here, behind the no-RBAC /auth/me/* endpoints.
export default function Profile({ tabId }: { tabId?: string }) {
    const { user, refreshUser } = useAuth()
    const { toast } = useToast()
    const { publishChange } = useTabs()
    // Collaborative edit lock keyed by the user record ('user:<id>') — the SAME key
    // the user management (UserForm) uses, so editing this account here and in the
    // admin user form at the same time (or on a second PC) mutually locks.
    const lock = useEditLock(user?._id ? `user:${user._id}` : null)
    const locked = !lock.canEdit
    const grantedToasted = useRef(false)
    // Bumps when another client saves this user record → re-pull /auth/me so the
    // name/role here reflect an edit made in the admin user form.
    const recordVersion = useDataVersion(user?._id ? `users:${user._id}` : '')
    const firstVersion = useRef(recordVersion)
    useEffect(() => {
        if (recordVersion === firstVersion.current) return
        firstVersion.current = recordVersion
        void refreshUser()
    }, [recordVersion, refreshUser])

    // Notify (once) when the lock is handed over after waiting.
    useEffect(() => {
        if (lock.justGranted && !grantedToasted.current) {
            grantedToasted.current = true
            toast(T.canEditNow)
        }
    }, [lock.justGranted, toast])

    // --- Name section (tracked by the tab dirty-guard) ---
    const [name, setName] = useState(user?.name ?? '')
    const [nameBusy, setNameBusy] = useState(false)
    const [nameError, setNameError] = useState('')
    const nameDirty = name.trim() !== (user?.name ?? '')
    const nameFormRef = useRef<HTMLFormElement>(null)
    const pwFormRef = useRef<HTMLFormElement>(null)

    // Keep the field in sync if the user is (re)loaded after mount.
    useEffect(() => { setName(user?.name ?? '') }, [user?.name])

    async function saveName(): Promise<SaveResult> {
        // `required` in the markup; the message stands at the field (forms/checkForm.ts).
        const invalid = checkBeforeSave(nameFormRef.current, setNameError)
        if (invalid) return invalid
        setNameBusy(true); setNameError('')
        try {
            await updateMyProfile(name.trim())
            await refreshUser()
            if (user?._id) publishChange('users', user._id) // notify the admin user form
            toast(T.nameSaved)
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setNameError(msg)
            return { ok: false, error: msg }
        } finally { setNameBusy(false) }
    }
    useDirtyGuard(tabId, nameDirty && !locked, saveName)

    // --- Password section (independent action, not part of the dirty-guard) ---
    const [current, setCurrent] = useState('')
    const [next, setNext] = useState('')
    const [pwBusy, setPwBusy] = useState(false)
    const [pwError, setPwError] = useState('')

    const pwWeak = !!next && !isPasswordAcceptable(next)
    // Empty fields are named at the field (form check); a weak password keeps the button off — the
    // meter and the hint under the field say why.
    const canChangePw = !pwBusy && !locked && (!next || isPasswordAcceptable(next))

    async function submitPassword(e: React.FormEvent) {
        e.preventDefault()
        if (checkBeforeSave(pwFormRef.current, setPwError)) return
        if (!canChangePw) return
        setPwBusy(true); setPwError('')
        try {
            await changeMyPassword(current, next)
            setCurrent(''); setNext('')
            toast(T.passwordChanged)
        } catch (e: any) {
            setPwError(e?.response?.data?.error ?? e?.message ?? T.passwordChangeFailed)
        } finally { setPwBusy(false) }
    }

    return (
        <div className="form-page">
            <div className="form-top">
                <div className="form-header"><h1>{T.title}</h1></div>
                {locked && (
                    <div className="notice warn">
                        {fill(T.lockedQueue, { position: lock.position > 0 ? fill(T.lockedQueuePosition, { position: lock.position }) : '' })}
                    </div>
                )}
            </div>

            <div className="form-body">
                <div className="panel">
                    <h2>{T.account}</h2>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.email}</label>
                            <input type="email" value={user?.email ?? ''} disabled />
                            <span className="hint">{T.emailHint}</span></div>
                        {/* Das LABEL, nicht der Schluessel („Administrator" statt „admin").
                            ⚠️ Es kommt aus /auth/me und NICHT aus der Rollenliste: die haengt
                            hinter requireCapability('roles','read'), diese Maske sieht aber
                            jeder Angemeldete. Ein zweiter Aufruf waere fuer alle ohne das
                            Modul ein 403 und fiele still auf genau den Schluessel zurueck,
                            der hier weg soll. */}
                        <div className="field" style={{ maxWidth: 220 }}><label>{T.role}</label>
                            <input type="text" value={user?.roleLabel || user?.role || ''} disabled /></div>
                    </div>
                </div>

                <form ref={nameFormRef} noValidate className="panel" onSubmit={(e) => { e.preventDefault(); void saveName() }}>
                    <h2>{T.changeName}</h2>
                    {nameError && <div className="notice err">{nameError}</div>}
                    <div className="row" style={{ alignItems: 'flex-end' }}>
                        <div className="field"><label>{T.name}</label>
                            <input type="text" value={name} required disabled={nameBusy || locked}
                                onChange={(e) => setName(e.target.value)} /></div>
                        <button type="submit" disabled={!nameDirty || nameBusy || locked}>
                            {nameBusy ? '…' : T.save}
                        </button>
                    </div>
                </form>

                <form ref={pwFormRef} noValidate className="panel" onSubmit={submitPassword}>
                    <h2>{T.changePassword}</h2>
                    {pwError && <div className="notice err">{pwError}</div>}
                    <div className="field" style={{ maxWidth: 320 }}>
                        <label>{T.currentPassword}</label>
                        <PasswordInput value={current} autoComplete="current-password" required
                            disabled={pwBusy || locked} onChange={setCurrent} />
                    </div>
                    <div className="field" style={{ maxWidth: 320 }}>
                        <label>{T.newPassword}</label>
                        <PasswordInput value={next} autoComplete="new-password" required
                            disabled={pwBusy || locked} onChange={setNext} />
                        <PasswordStrengthMeter password={next} />
                        {pwWeak && <span className="hint">{fill(T.passwordWeak, { min: MIN_PW })}</span>}
                    </div>
                    <div className="row">
                        <button type="submit" disabled={!canChangePw}>{pwBusy ? '…' : T.changePassword}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}
